import { useEffect, useMemo, useState } from "react";
import { devError } from "@/lib/log";
import { useSearchParams, Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import QRCode from "qrcode";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

// Printable A4 catalog sheet — 3×3 grid of products with photo, name,
// price and QR code linking to the public product page.
//
// Usage: /catalogo-imprimible?ids=id1,id2,...
export default function CatalogPrint() {
  const [params] = useSearchParams();
  const ids = useMemo(
    () => (params.get("ids") || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 9),
    [params],
  );
  const [products, setProducts] = useState([]);
  const [qrs, setQrs] = useState({}); // {id: dataURL}
  const [businessName, setBusinessName] = useState("Catálogo");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [list, cfg] = await Promise.all([
          api.get("/products"),
          api.get("/settings").catch(() => ({ data: {} })),
        ]);
        const filtered = (list.data || []).filter((p) => ids.includes(p.id));
        // Preserve the ids order so the user controls layout from inventory.
        filtered.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
        setProducts(filtered);
        if (cfg?.data?.nombre_negocio) setBusinessName(cfg.data.nombre_negocio);

        const origin = window.location.origin;
        const entries = await Promise.all(
          filtered.map(async (p) => {
            try {
              const url = `${origin}/p/${p.slug || p.id}`;
              const dataUrl = await QRCode.toDataURL(url, {
                width: 200,
                margin: 0,
                color: { dark: "#000000", light: "#FFFFFF" },
              });
              return [p.id, dataUrl];
            } catch {
              return [p.id, ""];
            }
          }),
        );
        setQrs(Object.fromEntries(entries));
      } catch (e) {
        devError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [ids]);

  const handlePrint = () => window.print();

  return (
    <div className="bg-zinc-100 min-h-screen" data-testid="catalog-print-page">
      {/* Toolbar — hidden when printing */}
      <div className="print:hidden bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between gap-2 sticky top-0 z-20">
        <Link
          to="/inventario"
          className="text-sm text-klein font-medium inline-flex items-center gap-1"
          data-testid="catalog-back"
        >
          <ArrowLeft size={14} /> Volver al inventario
        </Link>
        <div className="text-xs text-muted2 hidden sm:block">
          Hoja A4 · {products.length} producto{products.length === 1 ? "" : "s"} de {ids.length || 0}
        </div>
        <button
          onClick={handlePrint}
          className="btn-klein"
          data-testid="catalog-print-btn"
          disabled={loading || products.length === 0}
        >
          <Printer size={16} weight="bold" /> Imprimir
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-muted2">Cargando catálogo…</div>
      ) : products.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted2">
          No hay productos para imprimir. Vuelve al inventario y selecciona algunos.
        </div>
      ) : (
        <div className="mx-auto my-6 print:my-0 a4-sheet" data-testid="catalog-sheet">
          <header className="mb-4 print:mb-3 border-b-2 border-black pb-2 flex items-end justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-muted2">Catálogo</div>
              <div className="text-2xl font-black tracking-tighter leading-none">
                {businessName}
              </div>
            </div>
            <div className="text-right text-[10px] text-muted2 font-mono leading-tight">
              {new Date().toLocaleDateString("es-MX", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              <br />
              {window.location.host}
            </div>
          </header>

          <div className="grid grid-cols-3 gap-2 print:gap-1.5">
            {products.map((p) => {
              const imgs = p.imagenes_base64?.length
                ? p.imagenes_base64
                : p.imagen_base64
                  ? [p.imagen_base64]
                  : [];
              const thumb = imgs[0] ? `data:image/jpeg;base64,${imgs[0]}` : null;
              return (
                <div
                  key={p.id}
                  className="border border-black p-2 flex flex-col"
                  data-testid={`catalog-item-${p.id}`}
                >
                  <div className="aspect-square w-full bg-zinc-100 border border-zinc-300 overflow-hidden flex items-center justify-center mb-2">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={p.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-[10px] text-muted2">sin foto</span>
                    )}
                  </div>
                  <div className="font-bold text-[11px] leading-tight line-clamp-2 mb-1">
                    {p.nombre}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-muted2 mb-1">
                    {p.categoria}
                  </div>
                  <div className="text-base font-black tracking-tight text-klein mb-1 leading-none">
                    {fmt(p.precio_venta)}
                  </div>
                  <div className="mt-auto flex items-end justify-between gap-2">
                    <div className="text-[8px] text-muted2 font-mono leading-tight">
                      Escanea para ver
                      <br />
                      detalles y comprar
                    </div>
                    {qrs[p.id] && (
                      <img
                        src={qrs[p.id]}
                        alt="qr"
                        className="w-14 h-14"
                        data-testid={`catalog-qr-${p.id}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <footer className="mt-3 pt-2 border-t border-zinc-300 text-[9px] text-muted2 flex items-center justify-between">
            <span>{businessName}</span>
            <span>Hecho con VendeIA</span>
          </footer>
        </div>
      )}

      <style>{`
        .a4-sheet {
          width: 210mm;
          min-height: 297mm;
          padding: 14mm;
          background: white;
          box-shadow: 0 0 8px rgba(0,0,0,0.08);
        }
        @media print {
          body { background: white !important; }
          .a4-sheet { box-shadow: none; padding: 12mm; margin: 0 auto; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}
